/**
 * PJ 差分の状態と、その見せ方・文言の唯一の一次表現。
 *
 * 差分を出す画面は成果物の数だけ増える（フロー図・総合テスト・画面・業務）。状態の呼び方と
 * 色をそれぞれの画面が持つと、同じ「追加」が画面ごとに違う色と違う言葉で出る。実際にそうなって
 * いた — フロー図は追加を赤、総合テストの表は追加を緑で描き、片方は `created`、もう片方は
 * `added` と呼んでいた。読み手には同じ PJ の同じ変更なので、**画面をまたいだ瞬間に色の意味を
 * 学び直すことになる**。
 *
 * 状態の呼び方は変更ログの語彙（`created` / `updated` / `deleted`）に合わせる。差分の出どころが
 * 変更ログなので、画面だけ別の言葉を使うと突き合わせるときに翻訳が要る。`unchanged` は表に
 * 並べるときだけ要る 4 つ目で、変更ログには現れない。
 *
 * **色は「その本文がどちら側に在るか」で決める。入った側が緑、消えた側が赤である**
 * （[機能一覧](../../../../../docs/web/spec-console.md)）。GitHub の差分表示と同じ割り当てで、
 * 読み手の多くが毎日それを見ている。以前は赤 1 色で追加・変更の両方を塗り、削除は色を持た
 * なかったが、**左右に並べたときに左右が同じ色になり、位置以外に手掛かりが無かった**。
 *
 * 濃さは 2 段で持つ。**セル（その項目が変わった）と語（その中のどこが変わった）である。**
 * 1 段しか持たないと、セルを塗った時点で語を塗る余地が無くなる。色の値は
 * `styles.css` の `--color-diff-*` が持ち、ここはどの状態にどの役割を当てるかだけを持つ。
 *
 * 飾りの形は媒体で変わる。図のノードは帯と枠、表の行はタグとセルの地色である。ここが持つのは
 * **状態・文言・色の役割**で、帯にするかタグにするかは各画面が決める。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business PJ 差分の状態 4 種と、その文言・色の役割を 1 箇所で定める。
 * @graph-connects none
 */
import { Badge } from '@cortex/web-design-system/components/ui/Badge';
import { useTranslation } from 'react-i18next';

import type { DiffSide } from './DiffSplitPanes.js';

/** PJ 差分の状態。変更ログの語彙 3 つ + 表に並べるための「変更なし」。 @graph-connects none */
export const SPEC_DIFF_STATES = ['created', 'updated', 'deleted', 'unchanged'] as const;

/** PJ 差分の状態。 */
export type SpecDiffState = (typeof SPEC_DIFF_STATES)[number];

/** 状態 1 つの見せ方。 @graph-connects none */
interface SpecDiffView {
  /** 文言の翻訳キー。 */
  labelKey: string;
  /** タグの色。 */
  badge: 'default' | 'error' | 'success';
  /** タグを塗り潰すか。追加だけ塗り、変更は抜きにして同じ色のまま区別する。 */
  solid: boolean;
  /** 図のノード上端に敷く帯。持たない状態は null。 */
  bar: string | null;
  /** 図のノードの枠に足す指定。 */
  border: string;
  /** 表の行の本文に足す指定。左右比較の「変更前」側で、削除された行を打ち消す。 */
  text: string;
  /** 行そのものに敷く地色。追加・削除だけが持ち、変更は変わったセルだけを塗る。 */
  row: string;
}

/**
 * 状態ごとの見せ方。
 *
 * **追加と変更が緑なのは、どちらも「PJ が出たあとに在る」姿だからである。** 削除だけが赤で、
 * 変更前の側にしか出ない。区別はタグの文字と塗りの有無が担う（追加は塗り、変更は抜き）。
 *
 * 図では削除に破線の枠を足す。**枠には色を足さない** — 選択中の枠色と同じ要素で競合し、
 * 出力順しだいで負けた側が黙って消える。削除が赤であることは、タグと矢印線が言う。
 *
 * 行の地色を持つのは追加と削除だけである。変更は変わったセルだけを塗る。行ごと塗ると「行の
 * どこが変わったか」が消え、1 文字の直しと全面の書き換えが同じ見た目になる。
 * @graph-connects none
 */
const SPEC_DIFF_VIEW: Readonly<Record<SpecDiffState, SpecDiffView>> = {
  created: {
    labelKey: 'specDiff.created',
    badge: 'success',
    solid: true,
    bar: 'bg-[var(--color-diff-added-pane)]',
    border: '',
    text: '',
    row: 'bg-[var(--color-diff-added-cell)]',
  },
  updated: {
    labelKey: 'specDiff.updated',
    badge: 'success',
    solid: false,
    bar: 'bg-[var(--color-diff-added-pane)]',
    border: '',
    text: '',
    row: '',
  },
  deleted: {
    labelKey: 'specDiff.deleted',
    badge: 'error',
    solid: false,
    bar: null,
    border: 'border-dashed',
    text: 'line-through text-[var(--color-text-muted)]',
    row: 'bg-[var(--color-diff-removed-cell)]',
  },
  unchanged: {
    labelKey: 'specDiff.unchanged',
    badge: 'default',
    solid: false,
    bar: null,
    border: '',
    text: '',
    row: '',
  },
};

/**
 * 変わったセルに敷く地色。左が消えた側の赤、右が入った側の緑である。
 *
 * 行ではなくセル 1 つに掛ける。行ごと塗ると「行のどこが変わったか」が消え、1 文字の直しと
 * 全面の書き換えが同じ見た目になる。
 * @graph-connects none
 */
const SPEC_DIFF_CELL_CLASS: Readonly<Record<DiffSide, string>> = {
  before: 'bg-[var(--color-diff-removed-cell)]',
  after: 'bg-[var(--color-diff-added-cell)]',
};

/**
 * 変わった語に敷く地色。セルの地色より濃い段で、同じ側の色を使う。
 *
 * セルと同じ濃さにすると語が沈んで「セルが変わった」以上の情報を出せない。別の色にすると、
 * 同じ 1 つの変更が 2 色で語られる。**同じ色の濃い段**にするのが、GitHub が採っている形で
 * ある。
 * @graph-connects none
 */
export const SPEC_DIFF_WORD_CLASS: Readonly<Record<DiffSide, string>> = {
  before: 'bg-[var(--color-diff-removed-word)]',
  after: 'bg-[var(--color-diff-added-word)]',
};

/**
 * PJ が触った行に敷く地色。PJ モードの一覧で「自分の PJ はここに効いた」を拾わせる。
 *
 * 一覧の行は変更前を持たないので、消えた側の赤は出ない。入った側の緑だけを使う。
 * @graph-connects none
 */
export const SPEC_DIFF_TOUCHED_ROW_CLASS = SPEC_DIFF_CELL_CLASS.after;

/**
 * 変わったセルに足す class。変わっていなければ空文字。
 *
 * `side` は本文がどちら側の姿かを言う。左（変更前）なら消えた側の赤、右（変更後）なら入った
 * 側の緑になる。1 枚表示は PJ が出たあとの姿だけを出すので、既定の `after` でよい。
 *
 * @param changed そのセルが変わったか
 * @param side どちら側の姿か。1 枚表示では省略する
 * @returns セルに足す class（前置きの空白つき）。変わっていなければ空文字
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 変わったセルにだけ、その側の地色の class を足す。
 * @graph-connects none
 */
export function specDiffCellClass(changed: boolean, side: DiffSide = 'after'): string {
  return changed ? ` ${SPEC_DIFF_CELL_CLASS[side]}` : '';
}

/**
 * 追加・削除された行に足す地色の class。変更・変更なしの行は空文字。
 *
 * **追加と削除は行ごと塗る。** 行そのものが片側にしか無いので、「行のどこが変わったか」を
 * 問う余地が無い。変更された行だけがセル単位の問いを持つ（`specDiffCellClass`）。
 *
 * 側は取らない。追加された行は変更後にしか描かれず、削除された行は変更前にしか描かれない
 * （もう一方には空札が置かれる）ので、状態が決まれば色も決まる。
 *
 * @param state 差分の状態
 * @returns 行に足す class（前置きの空白つき）。地色を持たない状態は空文字
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 追加・削除された行にだけ行ごとの地色の class を足す。
 * @graph-connects none
 */
export function specDiffRowClass(state: SpecDiffState): string {
  const row = SPEC_DIFF_VIEW[state].row;
  return row === '' ? '' : ` ${row}`;
}

/**
 * 図のノードに足す飾り（上端の帯と枠の線種）。
 *
 * 表ではなく図が使う。帯は「上に何かが足された」形なので追加と変更にだけ付け、削除には
 * 付けない。
 *
 * @param state 差分の状態
 * @returns 帯の class（無ければ null）と枠に足す class
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 図のノードに足す帯と枠の指定を差分の状態から引く。
 * @graph-connects none
 */
export function specDiffNodeDecoration(state: SpecDiffState): {
  bar: string | null;
  border: string;
} {
  const view = SPEC_DIFF_VIEW[state];
  return { bar: view.bar, border: view.border };
}

/**
 * 表の行の本文に足す指定。削除された行を打ち消す。
 *
 * **効くのは左右比較の「変更前」側だけである。** 1 枚表示は PJ が出たあとの姿だけを出すので、
 * 削除された行がそもそも描画へ来ない（`toSingleViewRows`）。変更前の側では、その行が「もう
 * 実線ではない」ことを形で示す必要があるので打ち消す。
 *
 * @param state 差分の状態
 * @returns 本文に足す class
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 削除された行の本文に打ち消しの指定を返す。
 * @graph-connects none
 */
export function specDiffTextClass(state: SpecDiffState): string {
  return SPEC_DIFF_VIEW[state].text;
}

/** `SpecDiffBadge` の props。 */
export interface SpecDiffBadgeProps {
  /** 差分の状態。 */
  state: SpecDiffState;
  /** タグの大きさ。図のノードは小さい箱なので `sm` を渡す。 */
  size?: 'sm' | 'md';
}

/**
 * 差分の状態を表すタグ。図のノードにも表の行頭にも同じものを出す。
 *
 * **変更なしには何も描かない。** 印は「変わったところ」を拾うためのものなので、変わっていない
 * 行にまで付けると、印のある行を探す作業が印の無い行を探す作業に変わる。実際、PJ を選ぶと
 * 全行に「変更なし」が並び、どこが変わったのかは結局本文を読み比べることになっていた。
 * 図はもともと変更なしの状態を持たない（`FlowChangeState`）ので、表と一覧をそちらへ揃える。
 *
 * @graph-node {Function}
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business PJ 差分の状態をタグとして描く（変更なしには何も描かない）。
 * @graph-connects none
 */
export function SpecDiffBadge({ state, size = 'sm' }: SpecDiffBadgeProps) {
  const { t } = useTranslation();

  if (state === 'unchanged') return null;

  const view = SPEC_DIFF_VIEW[state];

  return (
    <Badge variant={view.badge} size={size} solid={view.solid}>
      {t(view.labelKey)}
    </Badge>
  );
}

/*
 * レーンのカードの印に「配下に変更」を足した語彙（`SpecLaneMark`）と、その札・枠
 * （`SpecLaneMarkBadge` ・ `specLaneMarkClass`）はここに在ったが、落とした。
 *
 * **PJ モードのどの画面にも「配下に変更」を出さない**（Dhik 2026-09-29「PJモードの業務マップでも
 * 配下に変更を出さず、色もつけないでほしい」の「でも」＝業務マップ以外も）。レーンのカードも見出しも、
 * 印は差分の 4 状態だけで描く。読取（`getChangeHistory` の `laneChanges` ・ `screenChanges`）は
 * `descendant` を返し続けるので、画面の入口（`pjCardMarkOf`）で変更なしに畳む。
 */

/*
 * 変更前の値を項目の下に添える部品（`BeforeValue`）はここに在ったが、落とした。
 *
 * **PJ モードで読みたいのは「その PJ が出たあとの仕様」である。** 変更前を同じ画面に重ねると、
 * 1 件の書き換えごとに 2 行ずつ積み上がり、公開後の姿を読むだけの人が毎行「どちらが結果か」を
 * 選り分けることになる。変更前と見比べたいときは「変更前と比較」で左右に割る（`DiffSplitPanes`）
 * ので、同じ事実を 1 枚表示にも重ねる理由が無い。
 */

/** `DiffDeletedHiddenNote` の props。 */
export interface DiffDeletedHiddenNoteProps {
  /** 出さなかった削除の件数。0 なら何も描かない。 */
  count: number;
  /** その画面に「変更前と比較」があるか。中身をどこで読めるかが変わる。 */
  hasCompare: boolean;
}

/**
 * 1 枚表示に出さなかった削除の件数。
 *
 * **消えたことまで黙らせない。** 削除された行を出さないのは「PJ が出たあとの姿」を読ませる
 * ためだが、件数すら出さないと、削除があったこと自体が読み手に届かない。読み手は自分が見て
 * いる一覧が全部なのか、消えたものがあるのかを判断できる必要がある。
 *
 * 中身の在り処は画面によって違う。「変更前と比較」を持つ画面はそこに出るので、そう案内する。
 * 持たない画面は PJ の変更一覧が持つので、そこには誘導せず「ここには出していない」とだけ言う
 * （押せない案内を書くと、探し先が無いのに探させる）。
 *
 * @graph-node {Function}
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 1 枚表示に出さなかった削除の件数を、中身の在り処つきで注記する。
 * @graph-connects none
 */
export function DiffDeletedHiddenNote({ count, hasCompare }: DiffDeletedHiddenNoteProps) {
  const { t } = useTranslation();

  if (count === 0) return null;

  return (
    <p data-deleted-hidden={count} className="text-xs text-[var(--color-text-muted)]">
      {t(hasCompare ? 'specDiff.deletedHiddenWithCompare' : 'specDiff.deletedHidden', {
        deleted: count,
      })}
    </p>
  );
}
